import React, { useState, useCallback } from 'react'
import type { EdgeInteractPlayerManager } from '@edge-interact/player-manager'

interface Conversation {
  id: string
  title: string
  lastMessage: string
  timestamp: string
  unread: number
}

interface Message {
  id: string
  sender: 'user' | 'assistant'
  content: string
  timestamp: string
}

interface ConversationsPageProps {
  playerManager?: EdgeInteractPlayerManager;
}

/**
 * ConversationsPage Component
 *
 * Displays list of conversations and chat interface.
 * Single responsibility: Manage conversation UI and messaging.
 *
 * Integration Points:
 * - Uses ChatWindow vanilla JS library
 * - Publishes messages via CommManager
 * - Receives responses via context
 * - Updates via PlayerManager state changes
 */
export default function ConversationsPage({ playerManager }: ConversationsPageProps): React.ReactElement {
  const [{ conversations, initialSelected }] = useState(() => {
    const id1 = `conv-${Math.random().toString(36).substr(2, 9)}`
    const id2 = `conv-${Math.random().toString(36).substr(2, 9)}`
    return {
      conversations: [
        {
          id: id1,
          title: 'General Support',
          lastMessage: 'How can I help you?',
          timestamp: new Date(Date.now() - 300000).toISOString(),
          unread: 0,
        },
        {
          id: id2,
          title: 'Technical Issues',
          lastMessage: 'I can assist with that',
          timestamp: new Date(Date.now() - 3600000).toISOString(),
          unread: 2,
        },
      ] as Conversation[],
      initialSelected: id1,
    }
  })

  const [conversationList, setConversations] = useState<Conversation[]>(conversations)
  const [selectedConversation, setSelectedConversation] = useState<string | null>(initialSelected)

  const [messageInput, setMessageInput] = useState('')
  const [messages, setMessages] = useState<Message[]>([
    {
      id: `msg-${Date.now()}`,
      sender: 'assistant',
      content: 'Conversation ready for messaging. Context includes user, session, and entity data.',
      timestamp: new Date(Date.now() - 60000).toISOString(),
    },
  ])

  const handleSendMessage = useCallback(async () => {
    if (!messageInput.trim()) return

    const userMessage: Message = {
      id: `msg-${Date.now()}`,
      sender: 'user',
      content: messageInput,
      timestamp: new Date().toISOString(),
    }
    setMessages((prev) => [...prev, userMessage])
    setMessageInput('')

    if (playerManager && selectedConversation) {
      // Route through Play Manager → Edge Stream → SignalR
      await playerManager.sendChatMessage(selectedConversation, {
        text: messageInput,
        type: 'user',
      })
    } else {
      // Fallback: simulate response when playerManager not connected
      console.log('[ConversationsPage] No PlayerManager — simulating response')
      setTimeout(() => {
        const assistantMessage: Message = {
          id: `msg-${Date.now()}-response`,
          sender: 'assistant',
          content: `Received: "${messageInput}" (simulated — connect Play Manager for live responses)`,
          timestamp: new Date().toISOString(),
        }
        setMessages((prev) => [...prev, assistantMessage])
      }, 1000)
    }
  }, [messageInput, playerManager, selectedConversation])

  const handleSelectConversation = useCallback((id: string) => {
    setSelectedConversation(id)
  }, [])

  const selectedConv = conversationList.find(
    (c) => c.id === selectedConversation,
  )

  return (
    <div className="page-container">
      <div className="page-title">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
        Conversations
      </div>
      <p className="page-description">
        Chat with context-aware messaging. All messages carry user, session, and
        conversation context.
      </p>

      <div className="conv-layout">
        {/* Conversations List */}
        <div className="conv-list-panel">
          <div className="conv-list-header">
            <h3>Recent Conversations</h3>
          </div>

          <div className="conv-list-body">
            {conversationList.map((conv) => (
              <div
                key={conv.id}
                className={`conv-item ${selectedConversation === conv.id ? 'selected' : ''}`}
                onClick={() => handleSelectConversation(conv.id)}
              >
                <div className="conv-item-title-row">
                  <div className="conv-item-title">{conv.title}</div>
                  {conv.unread > 0 && (
                    <span className="conv-unread-badge">{conv.unread}</span>
                  )}
                </div>
                <div className="conv-item-preview">{conv.lastMessage}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Chat Area */}
        <div className="conv-chat-area">
          {selectedConv ? (
            <>
              <div>
                <h3>{selectedConv.title}</h3>
              </div>

              <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    style={{
                      padding: '12px',
                      backgroundColor:
                        msg.sender === 'user'
                          ? '#3A8C45'
                          : '#2a2a2a',
                      color:
                        msg.sender === 'user'
                          ? 'white'
                          : '#f0f0f0',
                      borderRadius: '8px',
                      alignSelf:
                        msg.sender === 'user'
                          ? 'flex-end'
                          : 'flex-start',
                      maxWidth: '70%',
                    }}
                  >
                    <p style={{ margin: 0, wordWrap: 'break-word', fontSize: '12px' }}>
                      {msg.content}
                    </p>
                    <div className="conv-message-time">
                      {new Date(msg.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ padding: '16px', borderTop: '1px solid #3a3a3a', display: 'flex', gap: '12px', backgroundColor: '#1e1e1e' }}>
                <input
                  type="text"
                  className="conv-input"
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') handleSendMessage()
                  }}
                  placeholder="Type a message..."
                />
                <button onClick={handleSendMessage} className="button" style={{ padding: '8px 16px', minWidth: '80px' }}>
                  Send
                </button>
              </div>
            </>
          ) : (
            <div className="conv-empty">
              Select a conversation to start messaging
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
